import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { inviteTeamMember } from '../../../services/teamService';

const AddTeamMemberModal = ({ onClose, onAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    department: '',
    jobTitle: '',
    role: 'viewer'
  });

  const roleOptions = [
    { value: 'admin', label: 'Admin - Full system access' },
    { value: 'operator', label: 'Operator - Manage projects & permits' },
    { value: 'viewer', label: 'Viewer - Read-only access' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.fullName?.trim()) { setError('Full name is required'); return; }
    if (!formData?.email?.trim()) { setError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) { setError('Please enter a valid email address'); return; }

    setLoading(true);
    setError('');
    try {
      await inviteTeamMember(formData);
      onAdded();
    } catch (err) {
      setError(err?.message || 'Failed to add team member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Add Team Member</h2>
              <p className="text-sm text-muted-foreground">Invite a new member to your organization</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Full Name *"
                  placeholder="e.g. Jane Smith"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Email Address *"
                  type="email"
                  placeholder="jane@company.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                />
              </div>
              <Input
                label="Phone"
                placeholder="(555) 000-0000"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
              />
              <Input
                label="Department"
                placeholder="e.g. Operations"
                value={formData?.department}
                onChange={(e) => handleInputChange('department', e?.target?.value)}
              />
              <div className="md:col-span-2">
                <Input
                  label="Job Title"
                  placeholder="e.g. Project Coordinator"
                  value={formData?.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  label="Role"
                  options={roleOptions}
                  value={formData?.role}
                  onChange={(value) => handleInputChange('role', value)}
                />
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                <Icon name="Info" size={12} className="inline mr-1" />
                The member will be added with <strong>Pending</strong> status. They can be activated once they set up their account.
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={loading}
            iconName="UserPlus"
            iconPosition="left"
          >
            Add Member
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTeamMemberModal;
